import React, { useState, useEffect, useCallback } from 'react';
import { getTransferLogs, getBags, getTransfersByBag } from './inventoryApi';
import TransferInventoryModal from './TransferInventoryModal';

const COLORS = {
  aquaBlue: '#11a8a4',
  darkGrey: '#3c3b3f',
  vividPurple: '#9440dd',
  white: '#ffffff',
  lightGrey: '#f3f4f6',
  mediumGrey: '#e5e7eb',
  darkText: '#1f2937',
  mediumText: '#6b7280',
  errorRed: '#ef4444',
};

const formatDate = (d) => {
  if (!d) return 'N/A';
  try { return new Date(d).toLocaleString(); } catch { return 'Invalid'; }
};

const InventoryTransferPage = () => {
  // ── All-transfers view ──
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Active bags for "Transfer" button ──
  const [activeBags, setActiveBags] = useState([]);
  const [bagsLoading, setBagsLoading] = useState(true);

  // ── Transfer modal state ──
  const [transferBag, setTransferBag] = useState(null);  // bag object | null

  // ── Per-bag drill-down ──
  const [selectedBagId, setSelectedBagId] = useState('');
  const [bagTransfers, setBagTransfers] = useState([]);
  const [bagTransfersLoading, setBagTransfersLoading] = useState(false);

  // ── Pagination ──
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 20;

  const fetchTransfers = useCallback(async (pageNum = 1) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getTransferLogs({ page: pageNum });
      // Handle both paginated ({ results, count }) and plain array responses
      if (Array.isArray(data)) {
        setTransfers(data);
        setTotalCount(data.length);
      } else {
        setTransfers(data.results || []);
        setTotalCount(data.count || 0);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load transfer logs');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchActiveBags = useCallback(async () => {
    setBagsLoading(true);
    try {
      const data = await getBags({ status: 'ACTIVE' });
      const bags = Array.isArray(data) ? data : (data.results || []);
      setActiveBags(bags);
    } catch {
      // non-critical
    } finally {
      setBagsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTransfers(1);
    fetchActiveBags();
  }, [fetchTransfers, fetchActiveBags]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchTransfers(newPage);
  };

  // ── Drill-down: load transfers for a specific bag ──
  const handleBagFilter = async (bagId) => {
    setSelectedBagId(bagId);
    if (!bagId) { setBagTransfers([]); return; }
    setBagTransfersLoading(true);
    try {
      const data = await getTransfersByBag(bagId);
      const results = Array.isArray(data) ? data : (data.results || []);
      setBagTransfers(results);
    } catch {
      setBagTransfers([]);
    } finally {
      setBagTransfersLoading(false);
    }
  };

  // ── After successful transfer ──
  const handleTransferSuccess = (updatedBag) => {
    setTransferBag(null);
    fetchTransfers(1);
    fetchActiveBags();
    alert(`✅ Bag BAG-${updatedBag.id} successfully transferred to ${updatedBag.current_technician_details?.name || 'new technician'}.`);
  };

  const displayedTransfers = selectedBagId ? bagTransfers : transfers;

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  return (
    <div style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ margin: '0 0 4px 0', fontSize: '22px', fontWeight: '700', color: COLORS.darkText }}>
            🔄 Inventory Transfer Management
          </h2>
          <p style={{ margin: 0, color: COLORS.mediumText, fontSize: '14px' }}>
            Transfer inventory bags between technicians and view full transfer history
          </p>
        </div>
        <button
          onClick={() => { fetchTransfers(1); fetchActiveBags(); }}
          style={{ backgroundColor: COLORS.aquaBlue, color: COLORS.white, border: 'none', borderRadius: '6px', padding: '9px 16px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}
        >
          ↻ Refresh
        </button>
      </div>

      {/* Active Bags — Initiate Transfer */}
      <div
        style={{
          backgroundColor: COLORS.white,
          borderRadius: '8px',
          border: `1px solid ${COLORS.mediumGrey}`,
          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.07)',
          marginBottom: '24px',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${COLORS.mediumGrey}`, backgroundColor: COLORS.lightGrey, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ width: '4px', height: '18px', backgroundColor: '#dc2626', borderRadius: '2px', display: 'inline-block' }} />
          <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: COLORS.darkText }}>Bags — Initiate Transfer</h3>
        </div>
        <div style={{ padding: '16px' }}>
          {bagsLoading ? (
            <p style={{ color: COLORS.mediumText, fontSize: '14px', margin: 0 }}>Loading bags…</p>
          ) : activeBags.length === 0 ? (
            <p style={{ color: COLORS.mediumText, fontSize: '14px', margin: 0 }}>No active inventory bags found.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ backgroundColor: COLORS.lightGrey }}>
                    {['Bag ID', 'Technician', 'Period', 'Items', 'Status', 'Action'].map((h) => (
                      <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: COLORS.mediumText, fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {activeBags.map((bag) => (
                    <tr key={bag.id} style={{ borderTop: `1px solid ${COLORS.lightGrey}` }}>
                      <td style={{ padding: '9px 10px', color: COLORS.darkText, fontWeight: '600' }}>BAG-{bag.id}</td>
                      <td style={{ padding: '9px 10px', color: COLORS.darkText }}>{bag.current_technician_details?.name || `Tech #${bag.current_technician}`}</td>
                      <td style={{ padding: '9px 10px', color: COLORS.mediumText }}>{bag.month}/{bag.year}</td>
                      <td style={{ padding: '9px 10px', color: COLORS.mediumText }}>{bag.inventory_items?.length || 0}</td>
                      <td style={{ padding: '9px 10px' }}>
                        <span style={{ backgroundColor: '#dcfce7', color: '#16a34a', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' }}>
                          {bag.status}
                        </span>
                      </td>
                      <td style={{ padding: '9px 10px' }}>
                        <button
                          onClick={() => setTransferBag(bag)}
                          style={{
                            backgroundColor: '#dc2626',
                            color: COLORS.white,
                            border: 'none',
                            borderRadius: '5px',
                            padding: '5px 12px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#b91c1c')}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#dc2626')}
                        >
                          🔄 Transfer
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Transfer History */}
      <div
        style={{
          backgroundColor: COLORS.white,
          borderRadius: '8px',
          border: `1px solid ${COLORS.mediumGrey}`,
          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.07)',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${COLORS.mediumGrey}`, backgroundColor: COLORS.lightGrey, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ width: '4px', height: '18px', backgroundColor: COLORS.aquaBlue, borderRadius: '2px', display: 'inline-block' }} />
            <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: COLORS.darkText }}>Transfer History</h3>
            {totalCount > 0 && !selectedBagId && (
              <span style={{ backgroundColor: '#dbeafe', color: '#1d4ed8', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' }}>
                {totalCount} total
              </span>
            )}
          </div>
          {/* Bag filter dropdown */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '12px', color: COLORS.mediumText, fontWeight: '600' }}>Filter by Bag:</label>
            <select
              value={selectedBagId}
              onChange={(e) => handleBagFilter(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: '6px', border: `1px solid ${COLORS.mediumGrey}`, fontSize: '13px', color: COLORS.darkText, cursor: 'pointer' }}
            >
              <option value="">— All Bags —</option>
              {activeBags.map((b) => (
                <option key={b.id} value={b.id}>BAG-{b.id} · {b.current_technician_details?.name || ''} · {b.month}/{b.year}</option>
              ))}
            </select>
            {selectedBagId && (
              <button
                onClick={() => handleBagFilter('')}
                style={{ background: 'none', border: 'none', color: COLORS.aquaBlue, cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}
              >
                Clear
              </button>
            )}
          </div>
        </div>

        <div style={{ padding: '16px' }}>
          {(loading && !selectedBagId) || (bagTransfersLoading && selectedBagId) ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
              <div style={{ border: '4px solid #e5e7eb', borderTop: `4px solid ${COLORS.aquaBlue}`, borderRadius: '50%', width: '28px', height: '28px', animation: 'spin 1s linear infinite' }} />
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <p style={{ color: '#ef4444', fontSize: '14px' }}>{error}</p>
              <button onClick={() => fetchTransfers(1)} style={{ color: COLORS.aquaBlue, background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px' }}>Retry</button>
            </div>
          ) : displayedTransfers.length === 0 ? (
            <p style={{ textAlign: 'center', color: COLORS.mediumText, padding: '24px 0', margin: 0, fontSize: '14px' }}>
              No transfer records found{selectedBagId ? ` for BAG-${selectedBagId}` : ''}.
            </p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ backgroundColor: COLORS.lightGrey }}>
                    {['#', 'Bag', 'From Technician', 'To Technician', 'Transferred By', 'Reason', 'Date'].map((h) => (
                      <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: COLORS.mediumText, fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {displayedTransfers.map((log) => (
                    <tr key={log.id} style={{ borderTop: `1px solid ${COLORS.lightGrey}` }}>
                      <td style={{ padding: '9px 10px', color: COLORS.mediumText, fontSize: '12px' }}>{log.id}</td>
                      <td style={{ padding: '9px 10px', color: COLORS.darkText, fontWeight: '600' }}>
                        BAG-{log.inventory_bag_details?.id || log.inventory_bag}
                        <div style={{ fontSize: '11px', color: COLORS.mediumText }}>
                          {log.inventory_bag_details?.month}/{log.inventory_bag_details?.year}
                        </div>
                      </td>
                      <td style={{ padding: '9px 10px', color: COLORS.darkText }}>
                        {log.from_technician_details?.name || `Tech #${log.from_technician}`}
                      </td>
                      <td style={{ padding: '9px 10px' }}>
                        <span style={{ color: '#16a34a', fontWeight: '600' }}>
                          {log.to_technician_details?.name || `Tech #${log.to_technician}`}
                        </span>
                      </td>
                      <td style={{ padding: '9px 10px', color: COLORS.mediumText }}>
                        {log.transferred_by_details?.name || 'System'}
                      </td>
                      <td style={{ padding: '9px 10px' }}>
                        <span style={{ backgroundColor: '#dbeafe', color: '#1d4ed8', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' }}>
                          {log.reason || 'N/A'}
                        </span>
                      </td>
                      <td style={{ padding: '9px 10px', color: COLORS.mediumText, whiteSpace: 'nowrap' }}>
                        {formatDate(log.transferred_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination (only for all-transfers view) */}
              {!selectedBagId && totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '16px' }}>
                  <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page <= 1}
                    style={{ padding: '6px 14px', borderRadius: '5px', border: `1px solid ${COLORS.mediumGrey}`, backgroundColor: page <= 1 ? COLORS.lightGrey : COLORS.white, color: page <= 1 ? COLORS.mediumText : COLORS.darkText, cursor: page <= 1 ? 'not-allowed' : 'pointer', fontSize: '13px' }}
                  >
                    ← Prev
                  </button>
                  <span style={{ fontSize: '13px', color: COLORS.mediumText }}>
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page >= totalPages}
                    style={{ padding: '6px 14px', borderRadius: '5px', border: `1px solid ${COLORS.mediumGrey}`, backgroundColor: page >= totalPages ? COLORS.lightGrey : COLORS.white, color: page >= totalPages ? COLORS.mediumText : COLORS.darkText, cursor: page >= totalPages ? 'not-allowed' : 'pointer', fontSize: '13px' }}
                  >
                    Next →
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Transfer Modal */}
      {transferBag && (
        <TransferInventoryModal
          bag={transferBag}
          onClose={() => setTransferBag(null)}
          onSuccess={handleTransferSuccess}
        />
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default InventoryTransferPage;